export type PipelineBucket = 'active' | 'potential' | 'closed'
export type PipelinePurpose = 'Purchase' | 'Refinance'

export type PipelineRecord = {
  id: string
  number: string
  borrower: string
  purpose: PipelinePurpose
  assistant: 'Handy N.' | 'Renee P.'
  amount: number
  bucket: PipelineBucket
  status: string
  /** Days ago the application was taken. */
  appTakenDaysAgo: number
  /** Days ago closing docs went out, when applicable. */
  docsOutDaysAgo?: number
  /** Days ago the file was submitted to underwriting, when applicable. */
  submittedDaysAgo?: number
}

export const activeStatuses = [
  'Approved w/ Conditions',
  'Appraisal Back',
  'CTC',
  'Scheduled',
] as const

export const potentialStatuses = ['Application Taken', 'Docs Signed'] as const

export const pipelineRecords: PipelineRecord[] = [
  {
    id: 'p1',
    number: 'LN-1191',
    borrower: 'Aaron Whitfield',
    purpose: 'Purchase',
    assistant: 'Handy N.',
    amount: 512000,
    bucket: 'active',
    status: 'Scheduled',
    appTakenDaysAgo: 2,
    docsOutDaysAgo: 1,
  },
  {
    id: 'p2',
    number: 'LN-1155',
    borrower: 'Robert & Ana Silva',
    purpose: 'Refinance',
    assistant: 'Renee P.',
    amount: 329500,
    bucket: 'active',
    status: 'Approved w/ Conditions',
    appTakenDaysAgo: 5,
  },
  {
    id: 'p3',
    number: 'LN-1176',
    borrower: 'Grace Lindqvist',
    purpose: 'Purchase',
    assistant: 'Renee P.',
    amount: 689000,
    bucket: 'active',
    status: 'Appraisal Back',
    appTakenDaysAgo: 12,
    submittedDaysAgo: 9,
  },
  {
    id: 'p4',
    number: 'LN-1168',
    borrower: 'Devon Price',
    purpose: 'Purchase',
    assistant: 'Handy N.',
    amount: 415000,
    bucket: 'active',
    status: 'CTC',
    appTakenDaysAgo: 18,
    docsOutDaysAgo: 10,
    submittedDaysAgo: 14,
  },
  {
    id: 'p5',
    number: 'LN-1184',
    borrower: 'Jasmine Cole',
    purpose: 'Purchase',
    assistant: 'Handy N.',
    amount: 275000,
    bucket: 'active',
    status: 'Scheduled',
    appTakenDaysAgo: 3,
  },
  {
    id: 'p6',
    number: 'LN-1203',
    borrower: 'Marcus Webb',
    purpose: 'Refinance',
    assistant: 'Renee P.',
    amount: 610000,
    bucket: 'potential',
    status: 'Application Taken',
    appTakenDaysAgo: 1,
  },
  {
    id: 'p7',
    number: 'LN-1199',
    borrower: 'Sandra Kim',
    purpose: 'Purchase',
    assistant: 'Handy N.',
    amount: 505000,
    bucket: 'potential',
    status: 'Application Taken',
    appTakenDaysAgo: 6,
  },
  {
    id: 'p8',
    number: 'LN-1187',
    borrower: 'Danielle Ferro',
    purpose: 'Purchase',
    assistant: 'Renee P.',
    amount: 385000,
    bucket: 'potential',
    status: 'Docs Signed',
    appTakenDaysAgo: 14,
    docsOutDaysAgo: 9,
  },
  {
    id: 'p9',
    number: 'LN-1172',
    borrower: 'Priya Natarajan',
    purpose: 'Purchase',
    assistant: 'Renee P.',
    amount: 450000,
    bucket: 'closed',
    status: 'Withdrawn',
    appTakenDaysAgo: 20,
  },
  {
    id: 'p10',
    number: 'LN-1164',
    borrower: 'Miguel Santos',
    purpose: 'Purchase',
    assistant: 'Handy N.',
    amount: 340000,
    bucket: 'closed',
    status: 'Funded',
    appTakenDaysAgo: 22,
    docsOutDaysAgo: 20,
    submittedDaysAgo: 21,
  },
  {
    id: 'p11',
    number: 'LN-1161',
    borrower: 'Alicia Grant',
    purpose: 'Refinance',
    assistant: 'Renee P.',
    amount: 298000,
    bucket: 'closed',
    status: 'Denied',
    appTakenDaysAgo: 24,
  },
  {
    id: 'p12',
    number: 'LN-1158',
    borrower: 'Tom Becker',
    purpose: 'Purchase',
    assistant: 'Handy N.',
    amount: 362000,
    bucket: 'closed',
    status: 'Withdrawn',
    appTakenDaysAgo: 26,
  },
  {
    id: 'p13',
    number: 'LN-1152',
    borrower: 'Nina Petrova',
    purpose: 'Refinance',
    assistant: 'Renee P.',
    amount: 244000,
    bucket: 'closed',
    status: 'Nurture',
    appTakenDaysAgo: 28,
  },
  {
    id: 'p14',
    number: 'LN-1149',
    borrower: 'Carl Whitmore',
    purpose: 'Purchase',
    assistant: 'Handy N.',
    amount: 521000,
    bucket: 'closed',
    status: 'Nurture',
    appTakenDaysAgo: 29,
  },
  {
    id: 'p15',
    number: 'LN-1146',
    borrower: 'Yolanda Reyes',
    purpose: 'Purchase',
    assistant: 'Renee P.',
    amount: 289500,
    bucket: 'closed',
    status: 'Denied',
    appTakenDaysAgo: 16,
  },
  {
    id: 'p16',
    number: 'LN-1143',
    borrower: 'Peter Osei',
    purpose: 'Refinance',
    assistant: 'Handy N.',
    amount: 415500,
    bucket: 'closed',
    status: 'Nurture',
    appTakenDaysAgo: 11,
  },
  {
    id: 'p17',
    number: 'LN-1139',
    borrower: 'Hannah Lowe',
    purpose: 'Purchase',
    assistant: 'Renee P.',
    amount: 333000,
    bucket: 'closed',
    status: 'Withdrawn',
    appTakenDaysAgo: 9,
  },
  {
    id: 'p18',
    number: 'LN-1136',
    borrower: 'Ray Donnelly',
    purpose: 'Purchase',
    assistant: 'Handy N.',
    amount: 476000,
    bucket: 'closed',
    status: 'Nurture',
    appTakenDaysAgo: 8,
  },
  {
    id: 'p19',
    number: 'LN-1120',
    borrower: 'Estelle Moreau',
    purpose: 'Refinance',
    assistant: 'Renee P.',
    amount: 268000,
    bucket: 'closed',
    status: 'Funded',
    appTakenDaysAgo: 41,
    docsOutDaysAgo: 38,
    submittedDaysAgo: 39,
  },
  {
    id: 'p20',
    number: 'LN-1104',
    borrower: 'Owen Bradley',
    purpose: 'Purchase',
    assistant: 'Handy N.',
    amount: 355000,
    bucket: 'closed',
    status: 'Funded',
    appTakenDaysAgo: 55,
    docsOutDaysAgo: 51,
    submittedDaysAgo: 53,
  },
]

export const sumVolume = (records: PipelineRecord[]): number =>
  records.reduce((total, record) => total + record.amount, 0)

export const formatVolume = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value)

export const withinDays = (daysAgo: number | undefined, window: number): boolean =>
  typeof daysAgo === 'number' && daysAgo <= window
